import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useSimpleEmailRefresh } from '@/hooks/useSimpleEmailRefresh';
import { RefreshCw, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { isUAT } from '@/lib/environment';

interface RefreshEmailsButtonV3Props {
  isOAuth?: boolean;
  oauthProvider?: string;
  isDataReady?: boolean;
}

/**
 * Simplified email refresh button using the new direct processing system.
 * No complex Celery/Redis dependencies, just direct execution with progress.
 */
export const RefreshEmailsButtonV3: React.FC<RefreshEmailsButtonV3Props> = ({ 
  isOAuth = false, 
  oauthProvider = 'google',
  isDataReady = false 
}) => {
  const { isRefreshing, progress, message, error, taskId, startRefresh, cancelRefresh } = useSimpleEmailRefresh();
  const { toast } = useToast();
  
  // Simple initial loading state for 6 seconds after mount
  const [isInitializing, setIsInitializing] = useState(true);
  
  useEffect(() => {
    // Show "Establishing session" for 6 seconds after component mounts
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 6000);
    
    return () => clearTimeout(timer);
  }, []);

  // Determine if button should be disabled
  const isButtonDisabled = useMemo(() => {
    // Never disable if currently refreshing (allows cancellation)
    if (isRefreshing) return false;
    
    // Disable during initialization period
    if (isInitializing) return true;
    
    // Otherwise enabled
    return false;
  }, [isRefreshing, isInitializing]);

  // Determine button content
  const getButtonContent = () => {
    if (isRefreshing) {
      return {
        icon: <X className="mr-2 h-4 w-4" />,
        text: 'Cancel Refresh',
        variant: 'destructive' as const
      };
    }
    
    if (isInitializing) {
      return {
        icon: <Loader2 className="mr-2 h-4 w-4 animate-spin" />,
        text: 'Establishing session...',
        variant: 'default' as const
      };
    }
    
    return {
      icon: <RefreshCw className="mr-2 h-4 w-4" />,
      text: 'Refresh Emails',
      variant: 'default' as const
    };
  };

  const handleRefresh = async () => {
    console.log('[RefreshV3] Starting simplified email refresh');
    try {
      await startRefresh(isOAuth, oauthProvider);
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to start email refresh",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    console.log('[RefreshV3] Cancelling email refresh');
    cancelRefresh();
  };

  // Show error alert if there's an error
  if (error && !isRefreshing) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const buttonContent = getButtonContent();

  return (
    <div className="space-y-3">
      <div className="flex flex-col items-center space-y-2">
        <Button
          onClick={isRefreshing ? handleCancel : handleRefresh}
          disabled={isButtonDisabled}
          variant={buttonContent.variant}
          className={`w-full sm:w-auto ${isButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {buttonContent.icon}
          {buttonContent.text}
        </Button>
        
        {/* Show task ID in UAT mode */}
        {isUAT() && taskId && (
          <span className="text-xs text-muted-foreground">
            Task: {taskId.slice(0, 8)}...
          </span>
        )}
      </div>

      {/* Progress bar removed - handled by EmailProgress component in EmailRefreshCard */}

      {/* Success message */}
      {!isRefreshing && progress === 100 && message && !error && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{message}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};